import { User } from "../api/User/User";
import { ContractInfoApi } from "../generated/PersonContractInfoApi";
import { KmxKampagnenTypusApi } from "../generated/VersicherungKmxKampagnenTypusApi";
import { AngebotsHerkunftArtApi } from "../generated/VersicherungAngebotsHerkunftArtApi";
import { MembershipProductStatus } from "../../enums/MembershipProductStatus";
import { MembershipTarifCode } from '../../enums/MembershipTarifCode';
import { MembershipSelectionType } from "./MembershipSelectionType";
import { AppUtils } from '../../utils/index';
/**
 * This model represents the base calculator input data.
 */
export class CalculatorFormModel {
	private rate: number;
	private productKey: string;
	private productToken: string;
	private membershipSelection: MembershipSelectionType;
	private membershipPriceTypes: Array<ContractInfoApi>;
	private versicherungsbeginn: string;
	private geburtsdatum: string;
	private memberhipStatus: MembershipProductStatus;
	private productDescription: string;
	private orderId: string;
	private bassFlag: boolean;
	private bassAlreadyLoaded: boolean;
	private bassKmxCampaignType: KmxKampagnenTypusApi;
	private angebotsHerkunft: AngebotsHerkunftArtApi;
	private bassAdvertisingKey: string;
	private voucherMail: string;

	/**
	 * Optional birthday in iso format of the insurer.
	 * Please use a different birthday field for a differing insurant, because this date is passed to the 1-stufigen.
	 */
	get Geburtsdatum(): string {
		return this.geburtsdatum;
	}

	/**
	 * Optional birthday in iso format of the insurer.
	 * Please use a different birthday field for a differing insurant, because this date is passed to the 1-stufigen.
	 */
	set Geburtsdatum(value: string) {
		this.geburtsdatum = value;
	}

	get Versicherungsbeginn(): string {
		return this.versicherungsbeginn;
	}

	set Versicherungsbeginn(value: string) {
		this.versicherungsbeginn = value;
	}

	get MembershipSelection(): MembershipSelectionType {
		return this.membershipSelection;
	}

	set MembershipSelection(value: MembershipSelectionType) {
		this.membershipSelection = value;
	}

	/**
	 * The ProductKey for the selected insurance options.
	 */
	get ProductKey(): string {
		return this.productKey;
	}

	/**
	 * The ProductKey for the selected insurance options.
	 */
	set ProductKey(value: string) {
		this.productKey = value;
	}

	/**
	 * The ProductToken for the selected insurance options.
	 */
	get ProductToken(): string {
		return this.productToken;
	}

	/**
	 * The ProductToken for the selected insurance options.
	 */
	set ProductToken(value: string) {
		this.productToken = value;
	}

	/**
	 * The costs of the selected product.
	 * Whenever possible, the rate should be calculated and not stored.
	 * The stored value is mainly needed for the success page and provided by the form page.
	 */
	get Rate(): number {
		return this.rate;
	}

	/**
	 * The costs of the selected product.
	 * Whenever possible, the rate should be calculated and not stored.
	 * The stored value is mainly needed for the success page and provided by the form page.
	 */
	set Rate(value: number) {
		this.rate = value;
	}

	/**
	 * The OrderId for ecommerce
	 */
	get OrderId(): string {
		return this.orderId;
	}

	/**
	 * The OrderId for ecommerce
	 */
	set OrderId(value: string) {
		this.orderId = value;
	}

	get MembershipPriceTypes(): Array<ContractInfoApi> {
		return this.membershipPriceTypes;
	}

	set MembershipPriceTypes(value: Array<ContractInfoApi>) {
		this.membershipPriceTypes = value;
	}


	public get MemberhipStatus(): MembershipProductStatus {
		return this.memberhipStatus;
	}
	public set MemberhipStatus(value: MembershipProductStatus) {
		this.memberhipStatus = value;
	}

	public get ProductDescription(): string {
		return this.productDescription;
	}
	public set ProductDescription(value: string) {
		this.productDescription = value;
	}

	isValid(): boolean {
		return this.versicherungsbeginn !== undefined;
	}

	public getMglPrice(mglPriceType: MembershipTarifCode): number {
		return this.MembershipPriceTypes.filter(function (mglPrice: ContractInfoApi) {
			return mglPrice.TarifCode === mglPriceType.toString();
		})[0].Beitrag;
	}

	public getMglTarifcode(mglPriceType: MembershipTarifCode): number {
		return this.MembershipPriceTypes.filter(function (mglPrice: ContractInfoApi) {
			return mglPrice.TarifCode === mglPriceType.toString();
		})[0].TarifCodeId;
	}

	public get BassFlag(): boolean {
		return this.bassFlag;
	}
	public set BassFlag(value: boolean) {
		this.bassFlag = value;
	}

	public get BassAlreadyLoaded(): boolean {
		return this.bassAlreadyLoaded;
	}
	public set BassAlreadyLoaded(value: boolean) {
		this.bassAlreadyLoaded = value;
	}

	public get VoucherMail(): string {
		return this.voucherMail;
	}
	public set VoucherMail(value: string) {
		this.voucherMail = value;
	}

	get BassKmxCampaignType(): KmxKampagnenTypusApi {
		return this.bassKmxCampaignType;
	}

	set BassKmxCampaignType(value: KmxKampagnenTypusApi) {
		this.bassKmxCampaignType = value;
	}

	get AngebotsHerkunft(): AngebotsHerkunftArtApi {
		return this.angebotsHerkunft;
	}

	set AngebotsHerkunft(value: AngebotsHerkunftArtApi) {
		this.angebotsHerkunft = value;
	}

	get BassAdvertisingKey(): string {
		return this.bassAdvertisingKey;
	}

	set BassAdvertisingKey(value: string) {
		this.bassAdvertisingKey = value;
	}

	/**
	 * Overrides fields from the user object, if not already filled in the model.
	 * Non editable fields are always filled from the user.
	 *
	 * @param user the user object to merge. May be null or empty.
	 * @return the calculator model itself for convenient usage.
	 */
	public mergeWithUser(user: User): CalculatorFormModel {
		if (user && user.Valid && user.UserData.Geburtsdatum) {
			this.geburtsdatum = user.UserData.Geburtsdatum;
		}
		return this;
	}

	/**
	 * Creates a new CalculatorFormModel object and copies the values from the provided data object (or other form model) to the created CalculatorFormModel.
	 */
	public static createFromObject(dataObject: { [key: string]: any }): CalculatorFormModel {
		let array: Array<ContractInfoApi>;
		return AppUtils.Helper.createFromMap(dataObject, CalculatorFormModel, (key: string, value: any) => {
			switch (key.toLowerCase()) { //NOSONAR
				case 'membershippricetypes':
					if (value) {
						array = [];
						$.each(value, (i: number, item: ContractInfoApi) => {
							array.push(ContractInfoApi.createFromObject(item));
						});
						return array;
					}
					return [];
				default:
					return value;
			}
		});
	}
}


